import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ArcadeCursor = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isClicking, setIsClicking] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [trail, setTrail] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if device supports hover (not touch-only)
    const hasHover = window.matchMedia('(hover: hover)').matches;
    if (!hasHover) return;

    setIsVisible(true);

    const updateMousePosition = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });

      // Add trail point
      setTrail(prev => {
        const newTrail = [...prev, { x: e.clientX, y: e.clientY, id: Date.now() }];
        return newTrail.slice(-8); // Keep last 8 points
      });
    };

    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);

    const handleMouseOver = (e) => {
      const target = e.target;
      const isInteractive =
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('[role="button"]') ||
        target.classList.contains('cursor-pointer');

      setIsHovering(isInteractive);
    };

    window.addEventListener('mousemove', updateMousePosition);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseover', handleMouseOver);

    // Hide default cursor
    document.body.style.cursor = 'none';
    document.querySelectorAll('a, button, input, textarea, select, [role="button"]').forEach(el => {
      el.style.cursor = 'none';
    });

    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseover', handleMouseOver);
      document.body.style.cursor = 'auto';
    };
  }, []);

  if (!isVisible) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999]">
      {/* Trail effect */}
      <AnimatePresence>
        {trail.map((point, index) => (
          <motion.div
            key={point.id}
            initial={{ opacity: 0.6, scale: 1 }}
            animate={{ opacity: 0, scale: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute w-2 h-2 rounded-full"
            style={{
              left: point.x - 4,
              top: point.y - 4,
              background: `rgba(255, 0, 128, ${0.3 + (index / trail.length) * 0.4})`,
              boxShadow: '0 0 6px rgba(255, 0, 128, 0.5)',
            }}
          />
        ))}
      </AnimatePresence>

      {/* Outer ring */}
      <motion.div
        className="absolute rounded-full border-2 border-neon-pink"
        animate={{
          left: mousePosition.x - (isHovering ? 24 : 16),
          top: mousePosition.y - (isHovering ? 24 : 16),
          width: isHovering ? 48 : 32,
          height: isHovering ? 48 : 32,
          scale: isClicking ? 0.8 : 1,
          borderColor: isHovering ? '#FFD700' : '#FF0080',
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 28,
          mass: 0.5,
        }}
        style={{
          boxShadow: isHovering
            ? '0 0 20px rgba(255, 215, 0, 0.5), inset 0 0 10px rgba(255, 215, 0, 0.2)'
            : '0 0 15px rgba(255, 0, 128, 0.4)',
        }}
      />

      {/* Inner cursor - pixelated crosshair style */}
      <motion.div
        className="absolute"
        animate={{
          left: mousePosition.x - 6,
          top: mousePosition.y - 6,
          scale: isClicking ? 1.5 : 1,
          rotate: isClicking ? 45 : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 800,
          damping: 35,
        }}
      >
        {/* Pixel crosshair */}
        <svg width="12" height="12" viewBox="0 0 12 12" className="drop-shadow-[0_0_4px_rgba(255,0,128,0.8)]">
          {/* Center pixel */}
          <rect x="5" y="5" width="2" height="2" fill={isHovering ? '#FFD700' : '#FF0080'} />
          {/* Top */}
          <rect x="5" y="1" width="2" height="3" fill={isHovering ? '#FFD700' : '#FF0080'} />
          {/* Bottom */}
          <rect x="5" y="8" width="2" height="3" fill={isHovering ? '#FFD700' : '#FF0080'} />
          {/* Left */}
          <rect x="1" y="5" width="3" height="2" fill={isHovering ? '#FFD700' : '#FF0080'} />
          {/* Right */}
          <rect x="8" y="5" width="3" height="2" fill={isHovering ? '#FFD700' : '#FF0080'} />
        </svg>
      </motion.div>

      {/* Click burst effect */}
      <AnimatePresence>
        {isClicking && (
          <>
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  opacity: 1,
                  scale: 0,
                  x: mousePosition.x - 4,
                  y: mousePosition.y - 4,
                }}
                animate={{
                  opacity: 0,
                  scale: 2,
                  x: mousePosition.x - 4 + Math.cos(i * Math.PI / 2) * 30,
                  y: mousePosition.y - 4 + Math.sin(i * Math.PI / 2) * 30,
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute w-2 h-2 bg-trophy-gold"
                style={{ boxShadow: '0 0 8px rgba(255, 215, 0, 0.8)' }}
              />
            ))}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArcadeCursor;
